import React, { useState, useEffect } from 'react';
import { Settings, Plus, Trash2, Save, X, Edit, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MenuConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [showAddField, setShowAddField] = useState(false);

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');
  const locationId = localStorage.getItem('current_location_id');

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_URL}/api/config/menu${locationId ? `?location_id=${locationId}` : ''}`;
      const res = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setCustomFields(data.custom_field_definitions || []);
      } else {
        setError('Failed to fetch menu configuration');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const configData = {
        organization_id: orgId,
        location_id: locationId || null,
        name: config?.name || 'Default Menu Configuration',
        description: config?.description || '',
        is_active: config?.is_active !== false,
        custom_field_definitions: customFields,
        pricing_rules: config?.pricing_rules || {},
        availability_schedules: config?.availability_schedules || {}
      };

      const res = await fetch(`${API_URL}/api/config/menu`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });

      if (res.ok) {
        setSuccess('Menu configuration saved successfully');
        await fetchConfiguration();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addCustomField = (field) => {
    const newField = {
      id: `field_${Date.now()}`,
      name: field.name,
      label: field.label || field.name,
      field_type: field.field_type || 'text',
      options: field.options || null,
      default_value: field.default_value || null,
      is_required: field.is_required || false,
      validation_regex: field.validation_regex || null,
      min_value: field.min_value || null,
      max_value: field.max_value || null
    };
    setCustomFields([...customFields, newField]);
    setShowAddField(false);
  };

  const updateCustomField = (id, updates) => {
    setCustomFields(customFields.map(field => 
      field.id === id ? { ...field, ...updates } : field
    ));
    setEditingField(null);
  };

  const deleteCustomField = (id) => {
    if (window.confirm('Are you sure you want to delete this custom field?')) {
      setCustomFields(customFields.filter(field => field.id !== id));
    }
  };

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'select', label: 'Select (Single)' },
    { value: 'multiselect', label: 'Select (Multiple)' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'date', label: 'Date' }
  ];

  if (loading && !config) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Menu Configuration</h1>
        <p className="text-gray-600">Configure custom fields and settings for your menu items</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Configuration Details */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Configuration Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input aria-label="Name"
              type="text"
              value={config?.name || ''}
              onChange={(e) => setConfig({ ...config, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Menu Configuration Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <input aria-label="Description"
              type="text"
              value={config?.description || ''}
              onChange={(e) => setConfig({ ...config, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional description"
            />
          </div>
        </div>
      </div>

      {/* Custom Fields */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Custom Fields</h2>
          <button
            onClick={() => setShowAddField(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Field</span>
          </button>
        </div>

        {showAddField && (
          <CustomFieldForm
            fieldTypes={fieldTypes}
            onSave={addCustomField}
            onCancel={() => setShowAddField(false)}
          />
        )}

        {customFields.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No custom fields defined. Add one to get started.</p>
        ) : (
          <div className="space-y-4">
            {customFields.map((field) => (
              <div key={field.id} className="border border-gray-200 rounded-lg p-4">
                {editingField === field.id ? (
                  <CustomFieldForm
                    field={field}
                    fieldTypes={fieldTypes}
                    onSave={(updates) => updateCustomField(field.id, updates)}
                    onCancel={() => setEditingField(null)}
                  />
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{field.label}</h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {fieldTypes.find(t => t.value === field.field_type)?.label || field.field_type}
                        </span>
                        {field.is_required && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Required</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">Field name: {field.name}</p>
                      {field.description && (
                        <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                      )}
                      {field.options && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">Options:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {field.options.map((opt, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs">
                                {typeof opt === 'string' ? opt : opt.label || opt.value}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingField(field.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteCustomField(field.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={fetchConfiguration}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={saveConfiguration}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Configuration</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const CustomFieldForm = ({ field, fieldTypes, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: field?.name || '',
    label: field?.label || '',
    field_type: field?.field_type || 'text',
    options: field?.options || [],
    default_value: field?.default_value || '',
    is_required: field?.is_required || false,
    validation_regex: field?.validation_regex || '',
    min_value: field?.min_value || '',
    max_value: field?.max_value || ''
  });

  const [newOption, setNewOption] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const fieldData = {
      ...formData,
      options: formData.field_type === 'select' || formData.field_type === 'multiselect' 
        ? formData.options 
        : null,
      min_value: formData.field_type === 'number' && formData.min_value 
        ? parseFloat(formData.min_value) 
        : null,
      max_value: formData.field_type === 'number' && formData.max_value 
        ? parseFloat(formData.max_value) 
        : null
    };
    onSave(fieldData);
  };

  const addOption = () => {
    if (newOption.trim()) {
      setFormData({
        ...formData,
        options: [...formData.options, newOption.trim()]
      });
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Field Name (ID)</label>
          <input aria-label="Field Name (ID)"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., spice_level"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
          <input aria-label="Label"
            type="text"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Spice Level"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Field Type</label>
        <select aria-label="Field Type"
          value={formData.field_type}
          onChange={(e) => setFormData({ ...formData, field_type: e.target.value, options: [] })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {fieldTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {(formData.field_type === 'select' || formData.field_type === 'multiselect') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
          <div className="flex space-x-2 mb-2">
            <input aria-label="Options"
              type="text"
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add option"
            />
            <button
              type="button"
              onClick={addOption}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.options.map((opt, idx) => (
              <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg flex items-center space-x-2">
                <span>{opt}</span>
                <button
                  type="button"
                  onClick={() => removeOption(idx)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {formData.field_type === 'number' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Min Value</label>
            <input aria-label="Min Value"
              type="number"
              value={formData.min_value}
              onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Value</label>
            <input aria-label="Max Value"
              type="number"
              value={formData.max_value}
              onChange={(e) => setFormData({ ...formData, max_value: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Default Value</label>
        <input aria-label="Default Value"
          type={formData.field_type === 'number' ? 'number' : 'text'}
          value={formData.default_value}
          onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Optional default value"
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="is_required"
          checked={formData.is_required}
          onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="is_required" className="text-sm font-medium text-gray-700">
          Required field
        </label>
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Save Field
        </button>
      </div>
    </form>
  );
};

export default MenuConfiguration;
